// ── What the bell should say ─────────────────────────────────────────────────
//
// The notification icon in the header did nothing at all — no click handler, no
// content. QA asked what it was for; this is the answer.
//
// DERIVED, NOT STORED. Every notification here is computed from state the app
// already holds: which sites are unconnected, how many plan credits are left
// before the period rolls, which tickets are waiting on the user. Nothing is
// persisted and nothing is marked "read", and that is deliberate — each of
// these is an ACTIONABLE condition that disappears the moment the user deals
// with it. Connect the site and its notice goes; reply to the ticket and its
// notice goes. A stored, dismissible feed would let someone dismiss "your site
// is not connected" while the site is still not connected, which is how a
// notification centre becomes noise people learn to ignore.
//
// The trade-off: there is no history and no "mark all read". Neither is worth a
// table until someone asks for a notification that is genuinely an event rather
// than a state.

import { isWebsiteConnected } from './websiteStatus';
import { buildCreditExpiryWarnings } from './creditExpiryWarnings';
import { calendarDaysUntil } from './dates';
import { isLiveSubscription } from './subscriptionState';
import { PLANS_PATH } from './routes';

/**
 * How close to expiry we start warning, in days.
 *
 * Re-exported rather than defined here: the threshold now belongs to
 * creditBreakdown.js, which computes against it and which this module depends
 * on. Owning it here as well produced an import cycle that silently disabled
 * every expiry warning.
 */
export { EXPIRING_SOON_DAYS as CREDIT_EXPIRY_WARNING_DAYS } from './creditBreakdown';

export const NOTIFICATION_KINDS = {
  WEBSITE_DISCONNECTED: 'website_disconnected',
  CREDITS_EXPIRING: 'credits_expiring',
  TICKET_REPLY: 'ticket_reply',
  CARD_SAVED: 'card_saved',
  TRIAL_ENDED: 'trial_ended',
};

/**
 * How long after subscribing the "your card is saved" notice is shown.
 *
 * This is the case the module note above anticipated — "a notification that is
 * genuinely an EVENT rather than a state". Saving the card happens once, at
 * subscription; it is not a condition the user can resolve, so the usual rule
 * (show it until it is dealt with) does not apply and would make it permanent
 * noise instead.
 *
 * A window is what makes it behave like an event without needing a table and a
 * read/unread flag: it appears when it is news and disappears when it stops
 * being news. Seven days covers someone who subscribes and does not open the
 * dashboard again that week.
 */
export const CARD_SAVED_NOTICE_DAYS = 7;

/**
 * How long the "your trial ended, you are on the Basic plan" notice is shown.
 *
 * The second EVENT in this file, and it takes a window for the same reason
 * CARD_SAVED does: the switch to Free happened once and cannot be "resolved",
 * so the usual rule here — show it until the user deals with it — would make it
 * permanent furniture rather than news.
 *
 * Fourteen rather than seven. A lapsed trial is a decision point, not a
 * receipt: someone who does not open the app for a week has not stopped needing
 * to know their plan changed, and the credits they may still hold are what they
 * would be deciding about. The card-saved notice is a confirmation of something
 * the user just did, which is why it can be shorter.
 */
export const TRIAL_ENDED_NOTICE_DAYS = 14;

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(d);
};

// Was Math.ceil over a millisecond difference, which inflated by a day for any
// fractional remainder and read a date-only value as midnight UTC — landing on
// the previous day west of Greenwich. Both are fixed once, in utils/dates.js,
// and shared with the Credits page rather than corrected twice.
const daysUntil = (value, now) => calendarDaysUntil(value, now);

/**
 * Build the notification list.
 *
 * Pure, so the rules can be tested without mounting the shell. Every input is
 * optional: a caller whose fetches have not landed yet simply gets fewer
 * notifications, never a crash and never a wrong one.
 *
 * @param websites        rows decorated by useWebsitesWithStatus
 * @param statusReady     that hook's flag — false means "not known yet"
 * @param lots            useCreditBalances().lots — credit batches with expiry dates
 * @param creditDetail    DEPRECATED. The pooled compatibility shim; kept only so
 *                        an un-updated caller does not break. Nothing reads it.
 * @param periodEnd       subscription.next_billing_date. No longer used for
 *                        credit expiry — credits outlive the period — but still
 *                        accepted so existing callers keep working.
 * @param tickets         the user's support tickets
 * @param trialEndsAt     userData.trial_ends_at — drives the moved-to-Free notice
 * @param now             injectable for tests
 * @returns Array<{ id, kind, message, actionLabel, path, severity }>
 */
export function buildNotifications({
  websites = [],
  statusReady = false,
  lots = [],
  creditDetail = null,
  periodEnd = null,
  tickets = [],
  subscription = null,
  // The account's trial end date, for the moved-to-Free notice. Absent for an
  // account that never had a trial, which correctly produces no notice.
  trialEndsAt = null,
  now = new Date(),
} = {}) {
  const out = [];

  // ── 1. Sites that are not connected ──────────────────────────────────────
  // Gated on statusReady: before the status query resolves every row reads
  // "not connected", and announcing that would tell a user with a perfectly
  // healthy site that it is broken, once per page load.
  if (statusReady) {
    // `now` is threaded in because the rule has a staleness window for sites
    // whose plugin heartbeats — without it this branch would read the real
    // clock while every other rule here reads the injected one.
    (websites || []).filter((w) => !isWebsiteConnected(w, now.getTime())).forEach((w) => {
      const domain = String(w.domain || '').replace(/^https?:\/\//, '');
      out.push({
        id: `${NOTIFICATION_KINDS.WEBSITE_DISCONNECTED}:${w.id}`,
        kind: NOTIFICATION_KINDS.WEBSITE_DISCONNECTED,
        message: `Your website ${domain} is not connected.`,
        actionLabel: 'Connect now',
        // Prefills the domain on the guide, the same deep link the Websites
        // tab's Connect action uses.
        path: `/quick-start-guide?domain=${encodeURIComponent(domain)}`,
        severity: 'warning',
      });
    });
  }

  // ── 2. Credits about to expire ───────────────────────────────────────────
  //
  // ── WHAT THIS REPLACES, AND THE BUG IT CARRIED ──────────────────────────
  // This used to count `Object.values(creditDetail).reduce(… includedRemaining)`
  // against the subscription's period end. Two things were wrong with it.
  //
  // The number was roughly TRIPLE the truth. `creditDetail` is a compatibility
  // shim from the credit-pool merge, and it assigns ONE object to all three of
  // its keys — so summing the values added the same figure three times. A user
  // with 1,139 plan credits was told 3,417 were expiring. It survived review
  // because the test fixture used three DISTINCT objects, a shape the hook
  // never produces; the regression test added alongside this uses the real one.
  //
  // And it warned against the wrong date. Plan credits do not lapse at the
  // period end — they last two billing cycles, and purchased credits last a
  // year — so the deadline it quoted belonged to a different event entirely.
  //
  // Both go away by reading the lots, which is where expiry actually lives. One
  // notification per expiring batch, soonest first, worded identically to the
  // two banners because all three come from buildCreditExpiryWarnings.
  buildCreditExpiryWarnings(lots, now).forEach((warning) => {
    out.push({
      // Keyed on the batch, so two kinds expiring in the same window are two
      // notifications rather than one overwriting the other.
      id: `${NOTIFICATION_KINDS.CREDITS_EXPIRING}:${warning.key}`,
      kind: NOTIFICATION_KINDS.CREDITS_EXPIRING,
      message: warning.message,
      actionLabel: 'View credits',
      path: '/credits',
      severity: 'warning',
    });
  });

  // ── 3. The card kept when they subscribed ────────────────────────────────
  // Subscribing stores the card on the payment provider so renewals can charge
  // it without asking again. The confirm dialog says so before the charge; this
  // is the after-the-fact half — the customer should not have to remember a
  // sentence they read once to know their card is on file.
  //
  // Suppressed once the window closes (see CARD_SAVED_NOTICE_DAYS) so it cannot
  // become a permanent fixture, and suppressed entirely for a subscription that
  // is winding down, where "we kept your card for renewals" would be wrong.
  const startedAt = subscription?.start_date || subscription?.created_at || null;
  if (startedAt && !subscription?.cancel_at_period_end) {
    const age = daysUntil(startedAt, now);
    // daysUntil is negative for a past date; age of 0..-N days means recent.
    if (age !== null && age <= 0 && age >= -CARD_SAVED_NOTICE_DAYS) {
      const renews = formatDate(periodEnd);
      out.push({
        id: `${NOTIFICATION_KINDS.CARD_SAVED}:${String(startedAt).slice(0, 10)}`,
        kind: NOTIFICATION_KINDS.CARD_SAVED,
        message: renews
          ? `Your card is saved for automatic renewals — your plan renews on ${renews}. You can remove it in Settings.`
          : 'Your card is saved for automatic renewals. You can remove it in Settings.',
        actionLabel: 'Manage payment methods',
        path: '/settings',
        severity: 'info',
      });
    }
  }

  // ── 4. Support tickets waiting on the user ───────────────────────────────
  // 'waiting_on_user' is set server-side when an admin replies, so it means
  // exactly "there is a new message for you" — no unread bookkeeping needed.
  (tickets || [])
    .filter((t) => t.status === 'waiting_on_user')
    .forEach((t) => {
      const ref = t.ticket_display_id || t.ticket_number || `#${t.id}`;
      out.push({
        id: `${NOTIFICATION_KINDS.TICKET_REPLY}:${t.id}`,
        kind: NOTIFICATION_KINDS.TICKET_REPLY,
        message: `There is a new message regarding your support ticket ${ref}.`,
        actionLabel: 'Open ticket',
        path: '/support',
        severity: 'info',
      });
    });

  // ── 5. The trial ended, and the account moved to Free ────────────────────
  //
  // The switch is automatic and silent: nothing on the way in tells the user it
  // has happened, and the first sign is usually a generation failing for want
  // of credits. This is the notice for it.
  //
  // An EVENT, so it takes a window (TRIAL_ENDED_NOTICE_DAYS) exactly as the
  // card-saved notice does — the move to Free cannot be "resolved", and without
  // a window it would sit in the bell for the life of the account.
  //
  // Suppressed for anyone with a LIVE subscription. That covers the two ways
  // the notice would otherwise be wrong: someone who subscribed before their
  // trial ran out (they never went to Free) and someone who subscribed after
  // (they were on Free, briefly, and telling them so now is stale news about a
  // state they have already left). isLiveSubscription is the shared answer to
  // "is a paid plan in force", so this cannot drift from the plan card.
  //
  // The credits line is deliberate: purchased credits survive the trial, and
  // this is the moment someone is most likely to assume they lost everything.
  if (trialEndsAt && !isLiveSubscription(subscription)) {
    const since = daysUntil(trialEndsAt, now);
    // Negative once the date has passed; 0 is the day itself.
    if (since !== null && since <= 0 && since >= -TRIAL_ENDED_NOTICE_DAYS) {
      out.push({
        // Keyed on the date so it is one notice, not one per render.
        id: `${NOTIFICATION_KINDS.TRIAL_ENDED}:${String(trialEndsAt).slice(0, 10)}`,
        kind: NOTIFICATION_KINDS.TRIAL_ENDED,
        message: 'Your free trial has ended — you are now on the Basic plan. '
          + 'Any credits you purchased are still yours.',
        actionLabel: 'See plans',
        path: PLANS_PATH,
        severity: 'warning',
      });
    }
  }

  return out;
}
